<template>
  <div class="form-container card shadow-sm p-3">
    <!-- Cabeçalho -->
    <div class="header d-flex justify-content-between align-items-center">
      <h4 class="form-title">
        {{ conta.id ? "Editar Conta Financeira" : "Nova Conta Financeira" }}
      </h4>
      <div>
        <button class="btn btn-secondary me-2" @click="voltar">Voltar</button>
        <button class="btn btn-success btn-sm" v-if="conta.id" @click="abrirExtrato">
          <i class="bi bi-receipt-cutoff"></i> Extrato
        </button>
      </div>
    </div>

    <div class="spacer"></div>



    <div v-if="errors.backend" class="alert alert-danger" role="alert" aria-live="polite">
      {{ errors.backend }}
    </div>

    <BaseForm
      :isFormValid="isFormValid"
      :isSubmitting="isSubmitting"
      @submit="salvarConta"
      @cancel="voltar"
    >
      <div class="form-section">
        <h5 class="section-title">Dados da Conta Financeira</h5>

        <!-- Responsável -->
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label for="responsavel" class="form-label">
                Responsável <span class="required">*</span>
              </label>
              <div class="d-flex">
                <input
                  id="responsavel"
                  type="text"
                  class="form-control pessoa-input disabled-input"
                  :value="conta.responsavel_nome || ''"
                  disabled
                />
                <button
                  class="btn btn-primary pessoa-icon-btn"
                  type="button"
                  @click="abrirModal"
                >
                  <i class="bi bi-person"></i>
                </button>
              </div>
              <span v-if="formSubmitted && errors.responsavel_id" class="error-message">
                {{ errors.responsavel_id }}
              </span>
            </div>
          </div>
        </div>


        <!-- Apelido, Instituição, Agência e Número da Conta -->
        <div class="row">
          <!-- Apelido -->
          <div class="col-md-3">
            <div class="form-group">
              <label for="apelido">Apelido <span class="required">*</span></label>
              <input
                id="apelido"
                type="text"
                v-model="conta.apelido"
                class="form-control"
                :class="{ 'error-input': formSubmitted && errors.apelido }"
                placeholder="Digite o apelido"
              />
              <span v-if="formSubmitted && errors.apelido" class="error-message">
                {{ errors.apelido }}
              </span>
            </div>
          </div>

          <!-- Instituição -->
          <div class="col-md-4">
            <div class="form-group">
              <label for="instituicao">Instituição <span class="required">*</span></label>
              <input
                id="instituicao"
                type="text"
                v-model="conta.instituicao"
                class="form-control"
                :class="{ 'error-input': formSubmitted && errors.instituicao }"
                placeholder="Ex: 001 - Banco do Brasil"
              />
              <span v-if="formSubmitted && errors.instituicao" class="error-message">
                {{ errors.instituicao }}
              </span>
            </div>
          </div>

          <!-- Agência -->
          <div class="col-md-2">
            <div class="form-group">
              <label for="agencia">Agência <span class="required">*</span></label>
              <input
                id="agencia"
                type="text"
                v-model="conta.agencia"
                class="form-control"
                :class="{ 'error-input': formSubmitted && errors.agencia }"
                placeholder="Digite a agência"
              />
              <span v-if="formSubmitted && errors.agencia" class="error-message">
                {{ errors.agencia }}
              </span>
            </div>
          </div>

          <!-- Número da Conta -->
          <div class="col-md-2">
            <div class="form-group">
              <label for="numero-conta">Número da Conta <span class="required">*</span></label>
              <input
                id="numero-conta"
                type="text"
                v-model="conta.numero_conta"
                class="form-control"
                :class="{ 'error-input': formSubmitted && errors.numero_conta }"
                placeholder="Digite o número da conta"
              />
              <span v-if="formSubmitted && errors.numero_conta" class="error-message">
                {{ errors.numero_conta }}
              </span>
            </div>
          </div>
        </div>

        <!-- Snapshot (saldo de referência) + Saldo Atual -->
        <div class="row">
          <!-- Saldo de Referência (snapshot) -->
          <div class="col-md-3">
            <div class="form-group">
              <label>Saldo de Referência</label>
              <input
                type="text"
                class="form-control"
                :value="formatCurrency(conta.saldo_atualizado)"
                disabled
              />
              <small class="text-muted">
                Atualizado em:
                {{ conta.saldo_atualizado_em ? formatDate(conta.saldo_atualizado_em) : "—" }}
              </small>
            </div>
          </div>

          <!-- Saldo Atual (cálculo on-the-fly) -->
          <div class="col-md-3">
            <div class="form-group">
              <label>Saldo Atual</label>
              <input
                type="text"
                class="form-control"
                :value="saldoLoading ? 'Calculando…' : formatCurrency(saldoInfo?.saldo_atual)"
                disabled
              />
              <small class="text-muted" v-if="!saldoLoading && saldoInfo">
                (+{{ formatCurrency(saldoInfo.total_receitas_pos || 0) }}
                / {{ formatCurrency(saldoInfo.total_despesas_pos || 0) }})
              </small>
              <small class="text-danger" v-if="saldoError">{{ saldoError }}</small>
            </div>
          </div>

          <!-- Conta padrão -->
          <div class="row">
            <div class="col-md-6">
              <div class="form-group mt-2">
                <div class="form-check">
                  <input
                    id="conta_padrao"
                    class="form-check-input"
                    type="checkbox"
                    v-model="conta.conta_padrao"
                  />
                  <small class="text-muted ms-2" for="conta_padrao">
                    Definir como conta padrão para movimentação financeira
                  </small>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
      <div class="form-section">
        <h6 class="section-title">Recursos da conta</h6>
        <div class="form-check">
          <input id="boletos_habilitados" v-model="conta.boletos_habilitados" type="checkbox"
            class="form-check-input" :disabled="!podeEditarConta" />
          <label class="form-check-label ms-2" for="boletos_habilitados">Habilitar emissão de boletos</label>
        </div>
        <small class="text-muted d-block">Permite utilizar esta conta para emissão e gestão de boletos.</small>
        <small v-if="conta.boletos_habilitados" class="text-muted d-block">Configure o provedor em Financeiro → Boletos → Configurações Bancárias.</small>
      </div>
    </BaseForm>


    <!-- Modal de Seleção de Responsável (PF/PJ) -->
    <div v-if="showModal" class="modal-overlay">
      <div class="modal-container">
        <!-- Cabeçalho -->
        <div class="modal-header">
          <h6>Pesquisa de Responsáveis</h6>
          <i class="bi bi-x-circle close-icon" @click="fecharModal"></i>
        </div>

        <!-- Toggle PF/PJ -->
        <div class="toggle-buttons mb-3">
          <button
            class="btn"
            :class="{ 'btn-primary': tipoSelecionado === 'fisica', 'btn-secondary': tipoSelecionado !== 'fisica' }"
            @click="alterarTipo('fisica')"
          >
            Pessoas Físicas
         </button>
          <button
            class="btn"
            :class="{ 'btn-primary': tipoSelecionado === 'juridica', 'btn-secondary': tipoSelecionado !== 'juridica' }"
            @click="alterarTipo('juridica')"
          >
            Pessoas Jurídicas
          </button>
        </div>

        <!-- Filtros -->
        <div class="modal-filters">
          <label class="filter-label">Filtros:</label>
          <div class="filter-group">
            <input
              type="text"
              v-model="buscaNome"
              placeholder="Nome / Razão Social"
              class="filter-input"
              @input="filtrarResponsaveis"
            />
            <input
              type="text"
              v-model="buscaDoc"
              :placeholder="tipoSelecionado === 'fisica' ? 'CPF' : 'CNPJ'"
              class="filter-input"
              @input="filtrarResponsaveis"
            />
            <button class="btn btn-secondary small-filter-btn" @click="limparFiltros" title="Limpar Filtro">
              <i class="bi bi-funnel"></i>
            </button>
          </div>
        </div>

        <!-- Tabela -->
        <div class="table-container">
          <table class="table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!responsaveisFiltrados.length">
                <td colspan="3" class="text-center">Nenhum registro encontrado.</td>
              </tr>
              <tr v-else v-for="item in responsaveisFiltrados" :key="item.id">
                <td>{{ tipoSelecionado === 'fisica' ? item.nome : item.razao_social }}</td>
                <td>{{ item.email || 'N/A' }}</td>
                <td>
                  <button class="btn btn-success btn-sm" @click="selecionarResponsavel(item)">
                    <i class="bi bi-check-lg"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginação -->
        <div class="modal-footer">
          <span class="total-registros">{{ totalRecords }} registros</span>
          <div class="pagination-container">
            <button
              class="btn btn-light pagination-btn"
              :disabled="pagina <= 1"
              @click="mudarPagina(pagina - 1)"
            >
              <i class="bi bi-chevron-left"></i>
            </button>
            <span class="pagination-text">Página {{ pagina }} de {{ totalPaginas }}</span>
            <button
              class="btn btn-light pagination-btn"
              :disabled="pagina >= totalPaginas"
              @click="mudarPagina(pagina + 1)"
            >
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ModalExtratoConta
    v-if="showExtrato && conta.id"
    :conta-id="Number(conta.id)"
    @close="fecharExtrato"
  />

</template>

<script>
import { reactive, ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "@/services/axios";
import { usePermissions } from "@/composables/usePermissions";
import BaseForm from "@/components/Layout/BaseForm.vue";
import ModalExtratoConta from "@/components/Financeiro/ContasFinanceiras/ModalExtratoConta.vue";

export default {
  components: { BaseForm, ModalExtratoConta },
  props: {
    id: { type: [String, Number], default: null } // <- adiciona isso
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { hasPermission, fetchPermissions } = usePermissions();
    const podeEditarConta = computed(() => hasPermission(route.params.id ? "alterar_contas_financeiras" : "adicionar_contas_financeiras"));

    // Estado reativo do formulário
    const conta = reactive({
      id: null,
      apelido: "",
      instituicao: "",
      responsavel_id: null,
      responsavel_nome: "",
      agencia: "",
      numero_conta: "",
      tipo_proprietario: null,   // 'Pessoa Física' | 'Pessoa Jurídica'
      // campos de snapshot (somente leitura na UI)
      saldo_atualizado: 0.0,
      saldo_atualizado_em: null,
      conta_padrao: false,
      boletos_habilitados: false,
    });

    const errors = reactive({});
    const formSubmitted = ref(false);
    const isSubmitting = ref(false);

    const showModal = ref(false);
    // Estado do modal PF/PJ (mesma lógica do FormularioAnimal.vue)
    const tipoSelecionado = ref("fisica");     // 'fisica' | 'juridica'
    const buscaNome = ref("");
    const buscaDoc = ref("");                  // CPF ou CNPJ conforme tipo
    const responsaveis = ref([]);
    const pagina = ref(1);
    const totalPaginas = ref(1);
    const totalRecords = ref(0);
    const responsaveisFiltrados = computed(() => responsaveis.value || []);

    // Validação do formulário
    const isFormValid = computed(() => {
      return !!(
        conta.apelido &&
        conta.instituicao &&
        conta.responsavel_id &&
        conta.tipo_proprietario &&
        conta.agencia &&
        conta.numero_conta
      );
    });

    // --- Saldo atual (via endpoint /saldo) ---
    const saldoInfo = ref(null);
    const saldoLoading = ref(false);
    const saldoError = ref("");

    const showExtrato = ref(false);
    const abrirExtrato = () => { if (conta.id) showExtrato.value = true; };
    const fecharExtrato = () => { showExtrato.value = false; };


    function formatCurrency(n) {
      if (n == null) return 'Desconhecido';
      const v = typeof n === "number" ? n : parseFloat(n || 0);
      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(isNaN(v) ? 0 : v);
    }

    function formatDate(d) {
      if (!d) return "—";
      try {
        // aceita Date, 'YYYY-MM-DD' ou ISO completo
        const s = typeof d === "string" ? d.trim() : d.toString();
        const iso = (typeof d === "string" && d.length === 10) ? `${s}T00:00:00` : s;
        const dt = new Date(iso);
        if (isNaN(dt.getTime())) return "—";
        return dt.toLocaleDateString("pt-BR");
      } catch {
        return "—";
      }
    }

    const carregarSaldo = async (id) => {
      saldoLoading.value = true;
      saldoError.value = "";
      try {
        const { data } = await axios.get(`/financeiro/contas-financeiras/${id}/saldo`);
        saldoInfo.value = data;
        // se o GET da conta não trouxer snapshot, sincroniza a UI com o que /saldo retornou
        if (data?.saldo_atualizado !== undefined) {
          conta.saldo_atualizado = data.saldo_atualizado;
          conta.saldo_atualizado_em = data.saldo_atualizado_em;
        }
      } catch (err) {
        saldoError.value = err?.response?.data?.error || "Falha ao calcular saldo.";
        saldoInfo.value = null;
      } finally {
        saldoLoading.value = false;
      }
    };

    // --- Carregamento de dados ---

    const carregarConta = async (id) => {
      try {
        const { data } = await axios.get(`/financeiro/contas-financeiras/${id}`);
        Object.assign(conta, {
          ...data,
          // fallback seguro — se o back ainda não incluir snapshot no GET,
          // os campos persistem com default e depois o carregarSaldo() ajusta.
          saldo_atualizado: data.saldo_atualizado ?? conta.saldo_atualizado,
          saldo_atualizado_em: data.saldo_atualizado_em ?? conta.saldo_atualizado_em
        });
        // calcula saldo atual
        await carregarSaldo(id);
      } catch (error) {
        console.error("Erro ao carregar dados da conta:", error);
      }
    };

    const carregarNovaPessoa = async (newPersonId) => {
      try {
        const { data } = await axios.get(
          `/cadastros/pessoas-fisicas/${newPersonId}`
        );
        conta.responsavel_id = data.id;
        conta.responsavel_nome = data.nome;
      } catch (error) {
        console.error("Erro ao carregar nova pessoa:", error);
      } finally {
        router.replace({ query: { ...route.query, newPersonId: undefined } });
      }
    };

    onMounted(() => {
      fetchPermissions();
      const contaId = route.params.id;
      if (contaId) {
        conta.id = +contaId;
        carregarConta(conta.id);
      }
      watch(
        () => conta.tipo_proprietario,
        (tp) => {
          if (tp === "Pessoa Física") tipoSelecionado.value = "fisica";
          else if (tp === "Pessoa Jurídica") tipoSelecionado.value = "juridica";
        },
        { immediate: true }
      );
      if (route.query.newPersonId) {
        carregarNovaPessoa(route.query.newPersonId);
      }
    });

    // --- Ações do usuário ---
    // ----- Modal PF/PJ -----
    const carregarResponsaveis = async () => {
      try {
        const endpoint = tipoSelecionado.value === "fisica"
          ? "/cadastros/pessoas-fisicas"
          : "/cadastros/pessoas-juridicas";

        const params = tipoSelecionado.value === "fisica"
          ? { nome: buscaNome.value || undefined, cpf: buscaDoc.value || undefined }
          : { razao_social: buscaNome.value || undefined, cnpj: buscaDoc.value || undefined };

        const { data } = await axios.get(endpoint, { params: { ...params, page: pagina.value } });
        responsaveis.value = data?.data || [];
        totalPaginas.value = data?.totalPages || 1;
        totalRecords.value = data?.totalRecords || 0;
      } catch (err) {
        console.error("Erro ao carregar responsáveis:", err);
        responsaveis.value = [];
      }
    };

    const filtrarResponsaveis = () => {
      pagina.value = 1;
      carregarResponsaveis();
    };

    const limparFiltros = () => {
      buscaNome.value = "";
      buscaDoc.value = "";
      pagina.value = 1;
      carregarResponsaveis();
    };

    const mudarPagina = (nova) => {
      if (nova > 0 && nova <= totalPaginas.value) {
        pagina.value = nova;
        carregarResponsaveis();
      }
    };

    const abrirModal = () => {
      showModal.value = true;
    };
    const fecharModal = () => {
      showModal.value = false;
    };

    const alterarTipo = (tipo) => {
      tipoSelecionado.value = tipo;
      pagina.value = 1;
      carregarResponsaveis();
    };

    watch(showModal, (aberto) => {
      if (aberto) {
        limparFiltros();        // reseta e carrega
      }
    });

    const selecionarResponsavel = (item) => {
      if (tipoSelecionado.value === "fisica") {
        conta.responsavel_id = item.id;
        conta.responsavel_nome = item.nome;
        conta.tipo_proprietario = 'Pessoa Física';
      } else {
        conta.responsavel_id = item.id;
        conta.responsavel_nome = item.razao_social;
        conta.tipo_proprietario = 'Pessoa Jurídica';
      }
      fecharModal();
    };

    const voltar = () => {
      router.push("/financeiro/contas-financeiras");
    };

    const salvarConta = async () => {
      formSubmitted.value = true;
      if (!podeEditarConta.value || !isFormValid.value || isSubmitting.value) return;

      isSubmitting.value = true;
      errors.backend = "";
      try {
        const url = conta.id
          ? `/financeiro/contas-financeiras/${conta.id}`
          : `/financeiro/contas-financeiras`;
        const method = conta.id ? "put" : "post";

        await axios[method](url, {
          apelido: conta.apelido,
          instituicao: conta.instituicao,
          responsavel_id: conta.responsavel_id,
          tipo_proprietario: conta.tipo_proprietario,
          agencia: conta.agencia,
          numero_conta: conta.numero_conta,
          boletos_habilitados: conta.boletos_habilitados,
          conta_padrao: conta.conta_padrao
        });

        router.push("/financeiro/contas-financeiras");
      } catch (error) {
        errors.backend =
          error.response?.data?.error || error.response?.data?.message ||
          (error.response?.status === 403 ? "Você não possui permissão para salvar esta conta financeira." : "Erro ao salvar a conta financeira.");
        console.error("Erro ao salvar conta:", error);
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      podeEditarConta,
      conta,
      errors,
      formSubmitted,
      isFormValid,
      isSubmitting,
      showModal,

      // Modal PF/PJ
      tipoSelecionado,
      buscaNome,
      buscaDoc,
      responsaveisFiltrados,
      pagina,
      totalPaginas,
      totalRecords,
      carregarResponsaveis,
      filtrarResponsaveis,
      limparFiltros,
      mudarPagina,
      alterarTipo,
      selecionarResponsavel,

      showExtrato,
      abrirExtrato,
      fecharExtrato,

      saldoInfo,
      saldoLoading,
      saldoError,
      formatDate,
      formatCurrency,
      abrirModal,
      fecharModal,
      salvarConta,
      voltar
    };
  }
};
</script>


<style scoped>
/* ===== Formulário (já existia) ===== */
.form-container {
  margin: 20px;
}
.form-section {
  background: white;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  margin-bottom: 1rem;
}
.section-title {
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 10px;
}
.disabled-input {
  background: #e9ecef;
  border: 1px solid #ced4da;
}
.required {
  color: red;
}
.pessoa-input {
  flex: 1;
}
.pessoa-icon-btn {
  margin-left: 0px;
}
.spacer {
  margin-bottom: 20px;
}

/* ===== Modal (copiado e alinhado ao FormularioAnimal.vue) ===== */
.modal-overlay {
  position: fixed;
  inset: 0; /* top/left/right/bottom:0 */
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  background: #fff;
  width: 100%;
  max-width: 700px;
  height: 600px;
  overflow-y: auto;
  margin: 0 10px;
  border-radius: 4px;
}

.modal-header {
  background: #ccc;
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-icon {
  font-size: 1.5rem;
  cursor: pointer;
}

/* Filtros */
.modal-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 15px;
}

.filter-label {
  font-size: 12px;
  font-weight: bold;
  color: #333;
  margin-top: 10px;
  margin-left: 10px;
}

.filter-group {
  display: flex;
  flex-grow: 1;
  gap: 10px;
  margin-right: 40px;
}

.filter-input {
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 0px;
  font-size: 10px;
  outline: none;
  transition: 0.2s;
  margin-left: 8px;
  margin-right: 15px;
}

.filter-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(0, 123, 255, 0.3);
}

/* Tabela */
.table-container {
  margin-top: 15px;
  margin-left: 15px;
  margin-right: 15px;
}

.table th,
.table td {
  padding: 8px 10px;
  line-height: 1.2;
  vertical-align: middle;
}

.table td .btn-sm {
  padding: 4px 6px;
  font-size: 0.85rem;
  height: 15px;
  width: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.table th {
  background: #f1f1f1;
  font-weight: bold;
}

.table td {
  padding: 8px;
  border-bottom: 1px solid #ddd;
}

/* Rodapé / Paginação */
.modal-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  padding-top: 10px;
}

.pagination-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pagination-btn {
  padding: 6px 10px;
  font-size: 14px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-text {
  font-size: 12px;
  font-weight: normal;
  color: #333;
}

.total-registros {
  position: absolute;
  left: 20px;
  font-size: 14px;
  color: #555;
}

/* Botões auxiliares */
.filter-btn {
  background: #6c757d;
  border: none;
  color: white;
  padding: 6px 12px;
  font-size: 16px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter-btn i {
  font-size: 18px;
}

.filter-btn:hover {
  background: #1a1b1b;
}

.toggle-buttons {
  display: flex;
  justify-content: right;
  gap: 15px;
  margin-bottom: 0px;
  margin-top: 10px;
  margin-right: 40px;
}

.toggle-buttons .btn {
  padding: 4px 10px;
  font-size: 0.8rem;
}

.small-filter-btn {
  padding: 2px 10px;
  font-size: 12px;
  line-height: 1;
}

.small-filter-btn i {
  font-size: 10px;
}
</style>
